import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";
const RULES_BASE_URL = `${BASE_URL}/api/feeding-rules`;
const TASKS_BASE_URL = `${BASE_URL}/api/feeding-tasks`;
const LEGACY_FEEDING_BASE_URL = `${BASE_URL}/api/admin/feeding`;

const apiMsg = (e) =>
  e?.response?.data?.message ||
  e?.response?.data?.error ||
  (typeof e?.response?.data === "string" ? e.response.data : null) ||
  e?.message ||
  "Request failed";

function authHeaders() {
  const token = localStorage.getItem("authToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const clientFor = (baseURL) =>
  axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
  });

const toISODate = (d) => {
  if (!d) return undefined;
  if (typeof d === "string") return d.slice(0, 10);
  try {
    return new Date(d).toISOString().slice(0, 10);
  } catch {
    return undefined;
  }
};

const normalizeEnum = (v) => (v ? String(v).trim().toUpperCase() : undefined);

const toNumberOrNull = (v) => {
  if (v === "" || v === undefined || v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toIntegerOrNull = (v) => {
  const n = toNumberOrNull(v);
  return n === null ? null : Math.round(n);
};

const normalizeRule = (payload = {}) => {
  const inventoryItemId = toNumberOrNull(payload.inventoryItemId ?? payload.feedInventoryId);
  const out = {
    species: normalizeEnum(payload.species),
    projectType: normalizeEnum(payload.projectType || payload.batchGoal),
    minWeightKg: toNumberOrNull(payload.minWeightKg),
    maxWeightKg: toNumberOrNull(payload.maxWeightKg),
    gramsPerKgPerDay: toIntegerOrNull(payload.gramsPerKgPerDay),
    feedingTimesPerDay: payload.feedingTimesPerDay == null ? null : toIntegerOrNull(payload.feedingTimesPerDay),
    feedInventoryId: inventoryItemId,
    inventoryItemId,
    startDate: toISODate(payload.startDate),
    active: payload.active === undefined ? true : String(payload.active) === "true" || payload.active === true,
    notes: typeof payload.notes === "string" ? payload.notes.trim() : payload.notes || "",
    components: (payload.components || []).map(c => ({ inventoryId: Number(c.inventoryId), percentage: Number(c.percentage) })),
  };
  return out;
};

const normalizeTaskAction = (payload = {}) => ({
  status: normalizeEnum(payload.status),
  actualQuantity: toIntegerOrNull(payload.actualQuantity),
  actionDate: toISODate(payload.actionDate),
  rescheduledToDate: toISODate(payload.rescheduledToDate),
  conditionToday: normalizeEnum(payload.conditionToday),
  notes: typeof payload.notes === "string" ? payload.notes.trim() : payload.notes || "",
});

const taskDecisionPath = (status) => {
  const s = normalizeEnum(status);
  if (s === "GIVEN") return "given";
  if (s === "NOT_GIVEN") return "not-given";
  if (s === "SKIPPED") return "skip";
  if (s === "RESCHEDULED") return "reschedule";
  throw new Error(`Unsupported feeding task action: ${status}`);
};

const feedingService = {
  // -------- RULES --------
  listRules: async ({ species, projectType } = {}) => {
    try {
      const params = {};
      if (species) params.species = normalizeEnum(species);
      if (projectType) params.projectType = normalizeEnum(projectType);
      const { data } = await clientFor(RULES_BASE_URL).get("", { params });
      return Array.isArray(data) ? data : [];
    } catch (e) {
      console.error("listRules error:", apiMsg(e), e);
      throw e;
    }
  },

  createRule: async (payload) => {
    try {
      const { data } = await clientFor(RULES_BASE_URL).post("", normalizeRule(payload));
      return data;
    } catch (e) {
      console.error("createRule error:", apiMsg(e), e);
      throw e;
    }
  },

  updateRule: async (id, payload) => {
    try {
      const { data } = await clientFor(RULES_BASE_URL).put(`/${id}`, normalizeRule(payload));
      return data;
    } catch (e) {
      console.error("updateRule error:", apiMsg(e), e);
      throw e;
    }
  },

  deleteRule: async (id) => {
    try {
      await clientFor(RULES_BASE_URL).delete(`/${id}`);
    } catch (e) {
      console.error("deleteRule error:", apiMsg(e), e);
      throw e;
    }
  },

  // Manual generation was removed. This method is kept only to avoid crashes if an old component imports it.
  generateTasksForBatch: async () => {
    throw new Error("Manual feeding task generation was removed. Active feeding rules auto-create tasks by daily cron from start date.");
  },
  generateTasksByProjectType: async () => {
    throw new Error("Manual feeding task generation was removed. Active feeding rules auto-create tasks by daily cron from start date.");
  },
  runAutoFeeding: async (...args) => feedingService.generateTasksForBatch(...args),
  runAutoFeedingByProjectType: async (...args) => feedingService.generateTasksByProjectType(...args),

  syncTasks: async (date) => {
    const { data } = await clientFor(TASKS_BASE_URL).post('/sync', null, {
      params: { date: toISODate(date) },
    });
    return data;
  },
  bulkDecision: async (taskIds, action) => {
    const {data} = await clientFor(TASKS_BASE_URL).post('/bulk-decision', {taskIds,action:normalizeTaskAction(action)});
    return data;
  },
  completeTasksBulk: async (items) => {
    const { data } = await clientFor(TASKS_BASE_URL).post('/bulk-given', { items });
    return data;
  },

  // -------- TASK LIST / ACTION --------
  listTasks: async ({ batchId, species, projectType, date, from, to, status } = {}) => {
    try {
      const params = {};
      if (batchId) params.batchId = batchId;
      if (species) params.species = normalizeEnum(species);
      if (projectType) params.projectType = normalizeEnum(projectType);
      if (date) params.date = toISODate(date);
      if (from) params.from = toISODate(from);
      if (to) params.to = toISODate(to);
      if (status) params.status = normalizeEnum(status);
      const { data } = await clientFor(TASKS_BASE_URL).get("", { params });
      return Array.isArray(data) ? data : [];
    } catch (e) {
      console.error("listTasks error:", apiMsg(e), e);
      throw e;
    }
  },

  decideTask: async (taskId, payload) => {
    try {
      const body = normalizeTaskAction(payload);
      const path = taskDecisionPath(body.status);
      const { data } = await clientFor(TASKS_BASE_URL).patch(`/${taskId}/${path}`, body);
      return data;
    } catch (e) {
      console.error("decideTask error:", apiMsg(e), e);
      throw e;
    }
  },

  // -------- LEGACY HISTORY --------
  listLogs: async (batchId, { date } = {}) => {
    try {
      const { data } = await clientFor(LEGACY_FEEDING_BASE_URL).get(`/batches/${batchId}/logs`, {
        params: { date: toISODate(date) },
      });
      return Array.isArray(data) ? data : [];
    } catch (e) {
      console.error("listLogs error:", apiMsg(e), e);
      throw e;
    }
  },

  _apiMsg: apiMsg,
};

export default feedingService;
